import { Injectable, InjectionToken, Provider, WritableSignal, computed, inject, signal } from '@angular/core';
import { ToastConfig, ToastItem, ToastOptions, ToastPosition, ToastSeverity } from './toast.model';

const TOAST_EXIT_ANIMATION_MS = 260;

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  maxVisible: 5,
  defaultDuration: 5000,
  position: 'top-right',
  pauseOnHover: true,
  newestOnTop: true,
};

interface ToastTimer {
  timeoutId: ReturnType<typeof setTimeout>;
  startedAt: number;
  remaining: number;
}

export const TOAST_CONFIG = new InjectionToken<Partial<ToastConfig>>('TOAST_CONFIG');

export function provideToastConfig(config: Partial<ToastConfig>): Provider {
  return {
    provide: TOAST_CONFIG,
    useValue: config,
  };
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastConfig = inject(TOAST_CONFIG, { optional: true });

  private readonly configSignal: WritableSignal<ToastConfig> = signal({
    ...DEFAULT_TOAST_CONFIG,
    ...(this.toastConfig ?? {}),
  });

  private readonly visibleSignal = signal<ToastItem[]>([]);
  private readonly queueSignal = signal<ToastItem[]>([]);
  private readonly timers = new Map<string, ToastTimer>();

  readonly config = computed(() => this.configSignal());
  readonly toasts = computed(() => this.visibleSignal());
  readonly position = computed<ToastPosition>(() => this.config().position);

  configure(config: Partial<ToastConfig>): void {
    this.configSignal.update((current) => ({ ...current, ...config }));
    this.promoteQueuedToasts();
  }

  success(message: string): string;
  success(title: string, message: string): string;
  success(a: string, b?: string): string {
    return this.notify('success', a, b);
  }

  error(message: string): string;
  error(title: string, message: string): string;
  error(a: string, b?: string): string {
    return this.notify('error', a, b);
  }

  warning(message: string): string;
  warning(title: string, message: string): string;
  warning(a: string, b?: string): string {
    return this.notify('warning', a, b);
  }

  info(message: string): string;
  info(title: string, message: string): string;
  info(a: string, b?: string): string {
    return this.notify('info', a, b);
  }

  show(options: ToastOptions): string {
    const message = options.message?.trim();
    if (!message) {
      return '';
    }

    const severity = options.severity;
    const duration = Math.max(500, options.duration ?? this.config().defaultDuration);
    const title = options.title?.trim() || this.defaultTitle(severity);

    const toast: ToastItem = {
      id: this.createId(),
      title,
      message,
      severity,
      duration,
      closable: options.closable ?? true,
      persistent: options.persistent ?? false,
      exiting: false,
      paused: false,
    };

    if (this.visibleSignal().length < this.config().maxVisible) {
      this.addVisible(toast);
    } else {
      this.queueSignal.update((queue) => [...queue, toast]);
    }

    return toast.id;
  }

  dismiss(id: string): void {
    const currentToast = this.visibleSignal().find((toast) => toast.id === id);
    if (!currentToast || currentToast.exiting) {
      return;
    }

    this.clearTimer(id);
    this.visibleSignal.update((toasts) =>
      toasts.map((toast) => (toast.id === id ? { ...toast, exiting: true, paused: false } : toast)),
    );

    setTimeout(() => {
      this.visibleSignal.update((toasts) => toasts.filter((toast) => toast.id !== id));
      this.promoteQueuedToasts();
    }, TOAST_EXIT_ANIMATION_MS);
  }

  pause(id: string): void {
    if (!this.config().pauseOnHover) {
      return;
    }

    const timer = this.timers.get(id);
    if (!timer) {
      return;
    }

    clearTimeout(timer.timeoutId);
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));

    this.visibleSignal.update((toasts) =>
      toasts.map((toast) => (toast.id === id ? { ...toast, paused: true } : toast)),
    );
  }

  resume(id: string): void {
    if (!this.config().pauseOnHover) {
      return;
    }

    const timer = this.timers.get(id);
    if (!timer) {
      return;
    }

    this.visibleSignal.update((toasts) =>
      toasts.map((toast) => (toast.id === id ? { ...toast, paused: false } : toast)),
    );

    if (timer.remaining <= 0) {
      this.dismiss(id);
      return;
    }

    timer.startedAt = Date.now();
    timer.timeoutId = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  private notify(severity: ToastSeverity, a: string, b?: string): string {
    if (a.toLowerCase().includes('http')) {
      return this.show({ severity,  title: 'Something Went Wrong!!', message: "Our team is working on it." });
    }
    if (b === undefined) {
      return this.show({ severity, message: a });
    }

    return this.show({ severity, title: a, message: b });
  }

  private addVisible(toast: ToastItem): void {
    this.visibleSignal.update((toasts) => {
      if (this.config().newestOnTop) {
        return [toast, ...toasts];
      }
      return [...toasts, toast];
    });

    if (!toast.persistent) {
      this.startTimer(toast.id, toast.duration);
    }
  }

  private promoteQueuedToasts(): void {
    while (this.visibleSignal().length < this.config().maxVisible && this.queueSignal().length > 0) {
      const [nextToast, ...rest] = this.queueSignal();
      this.queueSignal.set(rest);
      this.addVisible(nextToast);
    }
  }

  private startTimer(id: string, duration: number): void {
    const timer: ToastTimer = {
      timeoutId: setTimeout(() => this.dismiss(id), duration),
      startedAt: Date.now(),
      remaining: duration,
    };

    this.timers.set(id, timer);
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (!timer) {
      return;
    }

    clearTimeout(timer.timeoutId);
    this.timers.delete(id);
  }

  private defaultTitle(severity: ToastSeverity): string {
    switch (severity) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
      default:
        return 'Info';
    }
  }

  private createId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
